import type { IndexPrerequisite } from './types';

/**
 * Joins a list into a readable clause — "categories", "categories and units", "categories,
 * item types and units".
 *
 * A sentence that reads "Add categories, item types, units" is a list of keys; this reads as
 * English, which is what a reader acts on.
 */
function joinWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  if (words.length === 2) return `${words[0]} and ${words[1]}`;

  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** What a shell needs to draw the banner and disable Create, from one derivation. */
export interface ComposedPrerequisites {
  /**
   * The ONE entry the banner is about, or null while nothing is missing.
   *
   * The first BLOCKING one where there is one: that is what is stopping the reader, and
   * leading with a catalog that is merely advisable while Create is disabled would be pointing
   * past the thing in their way.
   */
  lead: IndexPrerequisite | null;

  /** The banner's heading, about the lead alone. */
  title: string;

  /** The banner's body, about the lead alone, plus how many follow it. */
  description: string;

  /**
   * Why Create is disabled — naming ALL of the blocking ones, or null while none are.
   *
   * This is the one place the full list is still stated, and it does not contradict the
   * banner: the button answers *why can I not press this*, which is the whole list, and the
   * banner answers *what do I do now*, which is one step.
   */
  reason: string | null;
}

/**
 * ONE REQUIREMENT AT A TIME, derived once for every shell that draws the banner.
 *
 * Product call, 06 Oct 2026, in the reader's own words: *1 requirement at a time, do not
 * display all missing requirements in 1 banner*. A banner reading "Set up organizational
 * units, positions and the plantilla first" is a BACKLOG, not an instruction — it leaves the
 * reader to work out which end of it to start from, and on a fresh database that is every
 * module they open. Naming one leaves exactly one thing to do and one place to go.
 *
 * **Declaration ORDER is how a page says which comes first.** A page whose catalogs depend on
 * each other declares them deepest-first; most pages have one entry and never meet the
 * question.
 *
 * **It lives here rather than in either shell** because there are two: `<IndexPage>` and the
 * plantilla's deliberate fork of it. The fork's standing cost is that improvements to the
 * shared shell do not arrive there — this is the half of that cost which is avoidable, since a
 * sentence composed twice is a sentence that will be corrected once.
 */
export function composePrerequisites(
  prerequisites: readonly IndexPrerequisite[],
): ComposedPrerequisites {
  const unmet = prerequisites.filter((prerequisite) => !prerequisite.met);

  /*
    TWO KINDS, and only one of them withholds the form.

    A BLOCKING prerequisite is a required picker that would open empty, so Create is disabled
    with the sentence below. An ADVISORY one is a catalog the module is crippled without and
    the FORM does not need — Stock Levels registers items with no storage facility and then
    cannot record a single count — so it is named and the button is left alone.
  */
  const blocking = unmet.filter((p) => !p.advisory);
  const advisory = unmet.filter((p) => p.advisory);

  const lead = blocking[0] ?? advisory[0] ?? null;

  if (lead === null) return { lead: null, title: '', description: '', reason: null };

  /*
    How many are still to come AFTER the one on screen — both kinds, because the reader will
    meet all of them and the count is about work REMAINING rather than which sort each is.

    It is stated because the banner is otherwise silent about the chain: a reader who fills the
    catalog it names, returns, and meets a SECOND amber banner reads that as the system moving
    the goalposts. One clause makes the sequence legible, and it is still not a list of what
    they are.
  */
  const after = unmet.length - 1;

  const remainder =
    after <= 0
      ? ''
      : after === 1
        ? ' One more to set up after this one.'
        : ` ${after} more to set up after this one.`;

  return {
    lead,

    /*
      "first" is honest only on a BLOCKING entry: an advisory catalog is not something the next
      action waits on, and telling the reader to do it first would be the banner overruling the
      product call that left Create enabled.
    */
    title: lead.advisory ? `Set up ${lead.label}` : `Set up ${lead.label} first`,

    /*
      The LEAD's own sentence and nothing else's.

      An ADVISORY entry carries its own wording, because only the page knows what the catalog is
      FOR. A BLOCKING one is usually the same claim — the catalog is empty — so it is written
      here rather than retyped by every page; `detail` is the escape for the entry whose `met`
      asks a NARROWER question than "does this hold any row", where the default would state
      something false. See `IndexPrerequisite.detail`.
    */
    description: `${
      lead.advisory ??
      lead.detail ??
      `Nothing has been added to ${lead.label} yet, so the form cannot be completed.`
    }${remainder}`,

    /*
      "Set up", not "Add" — the verb the BANNER uses, so the two halves of one explanation speak
      one vocabulary rather than two. It also survives a label naming a page rather than a row:
      *"Add the plantilla before creating a record"* reads as adding the module, where posts are
      what get added to it.
    */
    reason:
      blocking.length === 0
        ? null
        : `Set up ${joinWords(blocking.map((p) => p.label))} before creating a record.`,
  };
}
